/*
 * Created on Wed Oct 11 2023 4:56:55 PM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023 
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */

import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { updateOrder } from '../../../../../../api/action/order';
import { processRefund } from '../../../../../../api/action/transaction';
import { isOrderCashPayment, isOrderZellePayment } from '../utils/orderUtils';
import { mergeCancellationOrderState } from '../utils/cancellationStateUtils';
import { getCancellationCopy } from '../utils/cancellationCopy';
import { academyLogger } from "utils/logger";

const CANCELLATION_INITIATED_BY_ATTRIBUTE = 'CANCELLATION_INITIATED_BY';
const CANCELLATION_REASON_ATTRIBUTE = 'CANCELLATION_REASON';

const useCancellation = (orders, setOrders, showSnackbar, options = {}) => {
    const dispatch = useDispatch();
    const companyDetails = useSelector((state) => state?.companyReducer?.company);
    const userDetail = useSelector((state) => state?.auth?.data) || {};
    const domainType = localStorage.getItem("domainType");
    const cancellationCopy = getCancellationCopy(options.isEducationDomain);

    const [cancellingOrderId, setCancellingOrderId] = useState(null);
    const [cancellationError, setCancellationError] = useState(null);
    const [refunding, setRefunding] = useState(false);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [orderToCancel, setOrderToCancel] = useState(null);

    const getTransactionFromOrder = (order) => {
        try {
            const transactions = order?.transactionList || [];
            if (transactions.length > 0) {
                const purchaseTransaction = transactions.find(t => t.type === 'PURCHASE' && t.status === 'SUCCESS');
                return purchaseTransaction || transactions[0];
            }
            return null;
        } catch (error) {
            return null;
        }
    };

    // Builds the dynamicAttributes payload recording who cancelled the booking and why.
    // Shared by every cancellation path so the backend has consistent audit data to act on
    // (including sending the guest cancellation email) regardless of how the order was paid.
    const buildCancellationDynamicAttributes = (order) => {
        const existingDynamicAttributes = Array.isArray(order?.dynamicAttributes)
            ? order.dynamicAttributes.filter(Boolean)
            : [];

        return [
            ...existingDynamicAttributes.filter((attribute) => {
                const name = String(attribute?.name || '').trim().toUpperCase();
                return name !== CANCELLATION_REASON_ATTRIBUTE
                    && name !== CANCELLATION_INITIATED_BY_ATTRIBUTE;
            }),
            { name: CANCELLATION_REASON_ATTRIBUTE, value: cancellationCopy.auditReason },
            { name: CANCELLATION_INITIATED_BY_ATTRIBUTE, value: 'USER' }
        ];
    };

    // Persists the cancellation on the order itself. This is what makes the status change
    // durable across a refresh and what triggers the backend's guest cancellation email —
    // a local-only status flip (setOrders) does neither.
    const persistCancellationStatus = (order, currentStatus) => dispatch(updateOrder({
        primaryId: order?.primaryId,
        currentStatus,
        modifiedBy: userDetail?.email || userDetail?.firstName || 'System',
        dynamicAttributes: buildCancellationDynamicAttributes(order)
    }, 'order_status_update'));

    const applyPersistedOrderStatus = (order, updatedOrder, fallbackStatus) => {
        setOrders((previousOrders) => mergeCancellationOrderState(
            previousOrders,
            order,
            updatedOrder,
            fallbackStatus
        ));
    };

    const processBookingRefund = async (order, transaction) => {
        try {
            setRefunding(true);
            setCancellationError(null);

            const paymentMethods = companyDetails?.account?.paymentMethods || [];
            const defaultPaymentMethod = paymentMethods.find(method => method.isDefault) || paymentMethods[0];

            if (!defaultPaymentMethod?.keySecret) {
                throw new Error('Payment gateway configuration not found');
            }

            if (!transaction) {
                throw new Error('Transaction not found in order data');
            }

            const userFullName = `${userDetail?.firstName || ''} ${userDetail?.lastName || ''}`.trim();
            const userId = userDetail?.primaryId || order?.userPrimaryId;
            const accountId = userDetail?.account?.primaryId || order?.accountId;

            const refundPayload = {
                paymentMethod: "PAYMENT_GATEWAY",
                currency: companyDetails?.operatingLocation?.code === 'US' ? 'USD' : 'INR',
                countryCode: companyDetails?.operatingLocation?.code || 'US',
                orderNumber: order?.primaryId || order?.orderNumber,
                userFullName: userFullName || 'Customer',
                userId: userId,
                accountId: accountId,
                amount: transaction.amount,
                paymentGatewayData: {
                    gatewayProvider: defaultPaymentMethod.paymentMethodType === 'PAYMENT_GATEWAY' ? 'Stripe' : defaultPaymentMethod.paymentMethodType,
                    customerName: userFullName || 'Customer',
                    customerEmail: userDetail?.email || order?.user?.email || ''
                },
                paymentIntentId: transaction.paymentRefId,
                refundReason: "ORDER_CANCELLED",
                company: {
                    companyName: companyDetails?.name || 'Company',
                    primaryId: companyDetails?.primaryId,
                    domain: { type: domainType },
                    account: {
                        paymentMethods: [{
                            paymentMethodType: defaultPaymentMethod.paymentMethodType,
                            isDefault: defaultPaymentMethod.isDefault,
                            keySecret: defaultPaymentMethod.keySecret
                        }]
                    }
                }
            };

            const refundResponse = await processRefund(refundPayload);

            let updatedOrder = null;
            try {
                // A successful refund is the user-visible outcome. Persisting the
                // order status is best-effort because a transient update failure
                // must not make an already completed refund look unsuccessful.
                updatedOrder = await persistCancellationStatus(order, 'REFUND_PENDING');
            } catch (updateError) {
                academyLogger.warn('Order status update failed after refund succeeded:', updateError);
            }

            applyPersistedOrderStatus(order, updatedOrder, 'REFUND_PENDING');
            setShowCancelDialog(false);
            setOrderToCancel(null);

            showSnackbar('Refund initiated successfully! It may take 5-10 business days to process.', 'success');
            return refundResponse;

        } catch (err) {
            academyLogger.error('Refund error:', err);

            if (err.message?.includes('already been refunded') ||
                err.message?.includes('charge_already_refunded')) {

                const updatedOrder = await persistCancellationStatus(order, 'CANCELLED');
                applyPersistedOrderStatus(order, updatedOrder, 'CANCELLED');
                setShowCancelDialog(false);
                setOrderToCancel(null);

                showSnackbar('This payment has already been refunded.', 'info');
                return null;
            }

            setCancellationError(err.message || cancellationCopy.refundFailed);
            showSnackbar(err.message || cancellationCopy.refundFailed, 'error');
            throw err;
        } finally {
            setRefunding(false);
        }
    };

    const cancelPendingPaymentBooking = async (order) => {
        const updatedOrder = await persistCancellationStatus(order, 'CANCELLED');
        applyPersistedOrderStatus(order, updatedOrder, 'CANCELLED');

        setShowCancelDialog(false);
        setOrderToCancel(null);
        showSnackbar(cancellationCopy.cancelledSuccess, 'success');
    };

    const initiateCancelBooking = (order) => {
        setOrderToCancel(order);
        setShowCancelDialog(true);
        setCancellationError(null);
    };

    const confirmCancelBooking = async () => {
        if (!orderToCancel) return;

        try {
            setCancellationError(null);
            setCancellingOrderId(orderToCancel.primaryId);

            const transaction = getTransactionFromOrder(orderToCancel);
            const orderStatus = orderToCancel?.currentStatus || orderToCancel?.status;
            const isCashOrder = isOrderCashPayment(orderToCancel);
            const isZelleOrder = isOrderZellePayment(orderToCancel);

            if (['PAYMENT_PENDING', 'PAYMENT_PROCESSING'].includes(orderStatus)) {
                if (isZelleOrder) {
                    throw new Error(cancellationCopy.zelleBlocked);
                }

                if (isCashOrder || !transaction) {
                    await cancelPendingPaymentBooking(orderToCancel);
                    return;
                }

                await cancelPendingPaymentBooking(orderToCancel);
                return;
            }

            if (transaction) {
                if (transaction.status === 'SUCCESS') {
                    await processBookingRefund(orderToCancel, transaction);
                } else {
                    throw new Error('This transaction cannot be refunded. Please contact support.');
                }
            } else {
                await cancelPendingPaymentBooking(orderToCancel);
            }
        } catch (err) {
            setCancellationError(err.message || cancellationCopy.cancellationFailed);
            showSnackbar(err.message || cancellationCopy.cancellationFailed, 'error');
        } finally {
            setCancellingOrderId(null);
        }
    };

    return {
        cancellingOrderId,
        cancellationError,
        refunding,
        showCancelDialog,
        orderToCancel,
        initiateCancelBooking,
        confirmCancelBooking,
        setShowCancelDialog,
        setOrderToCancel
    };
};

export default useCancellation;
